import * as React from 'react';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import ExportReportModal from '../../components/modals/ExportReportModal.tsx'; // ADDED .tsx extension
import RangeDatePicker from '../../components/Layout/DatePicker.tsx'; // Import the RangeDatePicker
import DatePicker from 'react-datepicker'; // Import standard DatePicker
import 'react-datepicker/dist/react-datepicker.css'; // Import datepicker styles
import '../../components/Layout/DatePicker.css'; // Import custom styles for single picker
// --- NEW: Import icons ---
import { CopyIcon, HashIcon, CheckIcon } from '../../components/icons/Icons.tsx'; // ADDED .tsx extension
import { Send, Download, Filter, RefreshCw, CheckCircle, ArrowRight, AlertTriangle, Clock, DollarSign, Users, FileText, Shield, X, Upload, Calendar } from 'react-feather';

// Mock Data - Define the structure and populate
interface Transaction {
  id: string;
  transactionDate: string;
  partner: string;
  product: string;
  type: string;
  amount: number;
  currency: string;
  status: 'Completed' | 'Pending' | 'Failed';
  location?: string;
  logoUrl?: string | null;
  // *** NEW: Add category field (optional, can be derived) ***
  category?: 'Credit' | 'Debit' | 'Correction';
  // *** NEW: Add fields for transaction details panel ***
  reference?: string;
  description?: string;
  reason?: string;
  approvedBy?: string;
  // NEW: Add field for correction details
  correctionDetails?: {
    correctionType: string;
    reason: string;
    documentUrl?: string;
    documentName?: string;
  };
}

// NEW: Partner data interface
interface Partner {
  name: string;
  location: string;
  logoUrl?: string | null;
}

// NEW: Define correction types
type CorrectionType = 
  | 'VAT Correction' 
  | 'Fee Adjustment' 
  | 'Commission Amount' 
  | 'Commission Start Date' 
  | 'Repayment';

// NEW: Modal Backdrop component interface
interface ModalBackdropProps {
  children: React.ReactNode;
  onClose: () => void;
  preventClose?: boolean;
}

// --- NEW: DropdownFilterProps interface ---
interface DropdownFilterProps {
    label: string;
    options: string[];
    selectedValue: string;
    onChange: (value: string) => void;
}

// --- NEW: DropdownFilter Component ---
const DropdownFilter: React.FC<DropdownFilterProps> = ({ label, options, selectedValue, onChange }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [hoveredOption, setHoveredOption] = React.useState<string | null>(null); // State for hover
    const dropdownRef = React.useRef<HTMLDivElement>(null); // Ref for the dropdown container

    const handleSelect = (option: string) => {
        onChange(option);
        setIsOpen(false);
    };

    // Effect to handle clicks outside the dropdown
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        // Add listener when dropdown is open
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            // Remove listener when dropdown is closed
            document.removeEventListener('mousedown', handleClickOutside);
        }

        // Cleanup listener on component unmount
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]); // Re-run effect when isOpen changes

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}> {/* Attach ref here */}
            <button
                style={{
                    padding: '8px 12px', // Match dashboard style
                    borderRadius: '6px',
                    border: '1px solid #d1d5db', // Keep light gray border
                    backgroundColor: 'white',
                    fontSize: '14px', // Match dashboard style
                    color: '#374151', // Match dashboard style
                    cursor: 'pointer',
                    minWidth: '140px', // Match dashboard style
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out' // Add transition
                }}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#adb5bd'} // Slightly darken border on hover
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'} // Restore border color
            >
                {label}: <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedValue}</span>
                {/* Custom Arrow */}
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? "m18 14-6-6-6 6" : "m6 10 6 6 6-6"} />
                </svg>
            </button>
            {isOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '160px', padding: '4px 0' }}> {/* Adjusted shadow and margin */}
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '200px', overflowY: 'auto' /* Added scroll for long lists */ }}>
                        {options.map(option => (
                            <li
                                key={option}
                                style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    backgroundColor: hoveredOption === option ? '#f9fafb' : 'white' // Lighter hover background
                                }}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => setHoveredOption(option)} // Set hover state
                                onMouseLeave={() => setHoveredOption(null)}   // Clear hover state
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// *** NEW: Partner Location Mapping ***
const partnerLocations: { [key: string]: string } = {
  'Amazon': 'UAE',
  'Shopify': 'KSA',
  'Noon': 'UAE',
  'Namshi': 'KSA',
  'Carrefour': 'UAE',
  'IKEA': 'KSA',
  'Adidas': 'UAE',
  'H&M': 'Qatar',
  'Xcite': 'Kuwait',
  'Jarir Bookstore': 'Kuwait',
  'Sharaf DG': 'Bahrain',
  'LuLu Hypermarket': 'Bahrain',
  'Talabat': 'Qatar',
  'Extra Electronics Superstore': 'KSA', // ADDED long name
  'Danube Hypermarket Online': 'UAE', // ADDED long name
};

// *** NEW: Partner Logo Mapping ***
// Using URLs from MerchantWalletDashboard.tsx
const partnerLogos: { [key: string]: string | null } = {
  'Amazon': 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
  'Shopify': 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg',
  'Noon': 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Noon_Website_Logo.svg', // Updated logo
  'Namshi': null, // No suitable logo found
  'Carrefour': 'https://upload.wikimedia.org/wikipedia/commons/5/5b/Carrefour_logo.svg',
  'IKEA': 'https://upload.wikimedia.org/wikipedia/commons/c/c5/Ikea_logo.svg',
  'Adidas': 'https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg',
  'H&M': 'https://upload.wikimedia.org/wikipedia/commons/5/53/H%26M-Logo.svg',
  'Xcite': 'https://media.xcite.com/media/logo/stores/1/New-Xcite-Logo-EnAr.png', // PNG found
  'Jarir Bookstore': 'https://upload.wikimedia.org/wikipedia/en/5/58/Jarir_Bookstore_logo.svg',
  'Sharaf DG': 'https://uae.sharafdg.com/wp-content/uploads/2020/11/logo-sharafdg.png', // PNG found
  'LuLu Hypermarket': null, // No suitable logo found
  'Talabat': 'https://upload.wikimedia.org/wikipedia/commons/b/b3/Talabat_logo.svg',
  'Extra Electronics Superstore': null, // ADDED long name - no logo
  'Danube Hypermarket Online': null, // ADDED long name - no logo
};

// *** NEW: Function to generate random ISO date string within a date range ***
const getRandomISODate = (baseDate: string, index: number): string => {
  const date = new Date(baseDate);
  // Add some day variation based on index (or keep dates as they are)
  // date.setDate(date.getDate() - (index % 5)); // Example: Vary date slightly

  const hour = Math.floor(Math.random() * 24); // Random hour (0-23)
  const minute = Math.floor(Math.random() * 60); // Random minute (0-59)
  const second = Math.floor(Math.random() * 60); // Random second (0-59)

  date.setUTCHours(hour, minute, second, 0); // Set UTC time

  return date.toISOString();
};

// *** NEW: Function to generate a simple UUID v4 ***
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// *** NEW: Enhance mock data with location, logo, AND RANDOM time ***
const rawMockTransactions: Omit<Transaction, 'id' | 'location' | 'logoUrl'>[] = [
  // Keep base dates, time will be randomized - REMOVE MANUAL IDs
  { transactionDate: '2025-04-21', partner: 'Amazon', product: 'BNPL', type: 'Sale', amount: 2500.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Amazon', product: 'BNPL', type: 'Refund', amount: -1200.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Amazon', product: 'BNPL', type: 'VAT', amount: 125.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Amazon', product: 'BNPL', type: 'Revenue corrections', amount: 150.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Shopify', product: 'Tabby Card', type: 'Sale', amount: -350.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Shopify', product: 'Tabby Card', type: 'Commission collection', amount: 17.50, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Shopify', product: 'Tabby Card', type: 'Refund', amount: -200.00, currency: 'SAR', status: 'Pending' },
  { transactionDate: '2025-04-24', partner: 'Shopify', product: 'Tabby Card', type: 'VAT', amount: 8.75, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Noon', product: 'Prepaid Ads', type: 'Top up', amount: 500.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Noon', product: 'Prepaid Ads', type: 'Spend', amount: -150.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Noon', product: 'Postpaid Ads', type: 'Ads top up', amount: 2000.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Noon', product: 'Postpaid Ads', type: 'Ads spend', amount: -600.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Namshi', product: 'Capital', type: 'Disbursement', amount: -50000.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Namshi', product: 'Capital', type: 'Repayment', amount: 55000.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Namshi', product: 'Capital', type: 'Late fees', amount: 750.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Namshi', product: 'Capital', type: 'VAT', amount: 38.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Carrefour', product: 'BNPL', type: 'Sale', amount: 1800.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Carrefour', product: 'BNPL', type: 'Collections', amount: 1200.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Carrefour', product: 'Acquiring', type: 'Sale', amount: 2300.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Carrefour', product: 'Acquiring', type: 'Refund', amount: -150.00, currency: 'AED', status: 'Failed' },
  { transactionDate: '2025-04-21', partner: 'IKEA', product: 'Capital', type: 'Disbursement', amount: -75000.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'IKEA', product: 'Prepaid Ads', type: 'Top up', amount: 1200.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'IKEA', product: 'Prepaid Ads', type: 'Spend', amount: -450.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'IKEA', product: 'Capital', type: 'VAT', amount: 200.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Adidas', product: 'Tabby Card', type: 'Sale', amount: -480.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Adidas', product: 'Tabby Card', type: 'Refund commission', amount: -24.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Adidas', product: 'Tabby Card', type: 'Revenue corrections', amount: 50.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Adidas', product: 'Tabby Card', type: 'Non-revenue corrections', amount: -30.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'H&M', product: 'Acquiring', type: 'Sale', amount: 3000.00, currency: 'QAR', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'H&M', product: 'Acquiring', type: 'VAT', amount: 150.00, currency: 'QAR', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'H&M', product: 'BNPL', type: 'Sale', amount: 1200.00, currency: 'QAR', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'H&M', product: 'BNPL', type: 'Refund', amount: -500.00, currency: 'QAR', status: 'Pending' },
  { transactionDate: '2025-04-21', partner: 'Xcite', product: 'BNPL', type: 'Sale', amount: 900.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Xcite', product: 'BNPL', type: 'Revenue corrections', amount: 80.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Xcite', product: 'BNPL', type: 'Non-revenue corrections', amount: -20.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Xcite', product: 'BNPL', type: 'Refund', amount: -300.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Jarir Bookstore', product: 'Capital', type: 'Disbursement', amount: -60000.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Jarir Bookstore', product: 'Capital', type: 'Repayment', amount: 70000.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Jarir Bookstore', product: 'Capital', type: 'Late repayment fee', amount: 500.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Jarir Bookstore', product: 'Capital', type: 'VAT on Fees', amount: 30.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Sharaf DG', product: 'Acquiring', type: 'Sale', amount: 1200.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Sharaf DG', product: 'Acquiring', type: 'Refund', amount: -100.00, currency: 'BHD', status: 'Pending' },
  { transactionDate: '2025-04-23', partner: 'Sharaf DG', product: 'Acquiring', type: 'VAT', amount: 60.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Sharaf DG', product: 'Acquiring', type: 'Revenue corrections', amount: 40.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'LuLu Hypermarket', product: 'BNPL', type: 'Sale', amount: 2500.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'LuLu Hypermarket', product: 'BNPL', type: 'Withdrawal', amount: -1000.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'LuLu Hypermarket', product: 'BNPL', type: 'Fixed Fee', amount: 50.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'LuLu Hypermarket', product: 'BNPL', type: 'VAT', amount: 125.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-21', partner: 'Talabat', product: 'Postpaid Ads', type: 'Ads top up', amount: 5000.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-22', partner: 'Talabat', product: 'Postpaid Ads', type: 'Ads spend', amount: -1200.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', partner: 'Talabat', product: 'Postpaid Ads', type: 'Non-revenue corrections', amount: -75.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-24', partner: 'Talabat', product: 'Postpaid Ads', type: 'Revenue corrections', amount: 100.00, currency: 'AED', status: 'Completed' },
  // --- ADDED Transactions with long partner names ---
  { transactionDate: '2025-04-25', partner: 'Extra Electronics Superstore', product: 'Tabby Card', type: 'Sale', amount: -750.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-26', partner: 'Extra Electronics Superstore', product: 'Tabby Card', type: 'Commission', amount: 37.50, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-27', partner: 'Danube Hypermarket Online', product: 'BNPL', type: 'Sale', amount: 450.00, currency: 'AED', status: 'Pending' },
  { transactionDate: '2025-04-28', partner: 'Danube Hypermarket Online', product: 'BNPL', type: 'VAT', amount: 22.50, currency: 'AED', status: 'Completed' },
];

// Process raw transactions to add random time, location, logo, and sort
const processedMockTransactions: Transaction[] = rawMockTransactions
  .map((tx, index) => ({
    ...tx,
    id: generateUUID(), // *** NEW: Generate UUID ***
    transactionDate: getRandomISODate(tx.transactionDate, index),
    location: partnerLocations[tx.partner] || 'N/A', // Add fallback for location
    logoUrl: partnerLogos[tx.partner], // Keep logo URL for now, even if not displayed
  }))
  .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());

// Helper function for currency formatting
const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

// Helper to get partner initials
const getInitials = (name: string): string => {
  if (!name) return '??';
  const words = name.split(' ').filter(Boolean); // Filter empty strings
  if (words.length === 0) return '??';
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];
  return (firstInitial + lastInitial).toUpperCase();
};

// Helper function to format date and time
const formatDateTime = (isoString: string): { date: string, time: string } => {
  try {
    const dateObj = new Date(isoString);
    // Format Date: e.g., Mar 31, 2025
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    // Format Time: e.g., 4:00 AM (lowercase am/pm)
    const formattedTime = dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).toLowerCase(); // Convert AM/PM to lowercase

    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    console.error("Error formatting date:", isoString, error);
    return { date: 'Invalid Date', time: '' }; // Fallback for invalid dates
  }
};

// Helper function to determine transaction category
const getTransactionCategory = (transaction: Transaction): 'Credit' | 'Debit' | 'Correction' => {
    // Prioritize 'correction' type
    if (transaction.type.toLowerCase().includes('correction')) {
        return 'Correction';
    }
    // Determine based on amount sign
    if (transaction.amount >= 0) { // Treat 0 as Credit for now, adjust if needed
        return 'Credit';
    } else {
        return 'Debit';
    }
};

// Status Badge Component (or inline styles)
const StatusBadge: React.FC<{ status: Transaction['status'] }> = ({ status }) => {
  let styles: React.CSSProperties = {
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'capitalize',
    display: 'inline-block', // Ensure badge takes only necessary width
  };

  switch (status) {
    case 'Completed':
      styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' };
      break;
    case 'Pending':
      styles = { ...styles, backgroundColor: '#fefce8', color: '#d97706' };
      break;
    case 'Failed':
      // Added styling for 'Failed' status based on conventions (e.g., red)
      styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' };
      break;
    default: // Optional default/fallback style
      styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' };
      break;
  }

  return <span style={styles}>{status}</span>;
};

// Category Badge Component
const CategoryBadge: React.FC<{ category: 'Credit' | 'Debit' | 'Correction' }> = ({ category }) => {
    let styles: React.CSSProperties = {
      // padding: '3px 10px', // Padding removed as background is gone
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
      textTransform: 'capitalize',
      display: 'inline-block',
    };

    switch (category) {
      case 'Credit':
        // Match Green from Dashboard's CR badge
        styles = { ...styles, /* backgroundColor: '#ecfdf5', */ color: '#059669' };
        break;
      case 'Debit':
         // Match Red from Dashboard's DR badge
        styles = { ...styles, /* backgroundColor: '#fef2f2', */ color: '#ef4444' };
        break;
      case 'Correction':
         // Match Purple from Dashboard's Manual Corrections badge
        styles = { ...styles, /* backgroundColor: '#eef2ff', */ color: '#4f46e5' };
        break;
      default:
        styles = { ...styles, /* backgroundColor: '#f8fafc', */ color: '#64748b' }; // Default gray
        break;
    }

    return <span style={styles}>{category}</span>;
};

// NEW: Modal Backdrop component
const ModalBackdrop: React.FC<ModalBackdropProps> = ({ children, onClose, preventClose = false }) => {
  const modalContentRef = React.useRef<HTMLDivElement>(null);

  // Handle clicks outside the modal content
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (preventClose) return;
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, preventClose]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '100%',
          maxWidth: '650px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </div>
    </div>
  );
};

// --- NEW: Transaction Details Panel Component ---
interface TransactionDetailsPanelProps {
  transaction: Transaction | null;
  onClose: () => void;
  isVisible: boolean;
  contentFadeKey: string; // Add a key to trigger content fade animation
}

const TransactionDetailsPanel: React.FC<TransactionDetailsPanelProps> = ({ transaction, onClose, isVisible, contentFadeKey }) => {
  // Create ref for the panel element - moved before conditional return
  const panelRef = React.useRef<HTMLDivElement>(null);
  
  // Add state for content fade animation
  const [contentVisible, setContentVisible] = React.useState(true);
  
  // Handle transaction changes with animation
  React.useEffect(() => {
    setContentVisible(true);
  }, [contentFadeKey]);
  
  // Handle outside click - moved before conditional return
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Skip outside click handling if we clicked on a table row (transaction)
      if ((event.target as Element).closest('tr')) {
        return;
      }
      
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    // Only add the event listener if the panel is visible
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Early return after hooks are called
  if (!transaction) return null;

  // Generate a transaction reference if not available
  const reference = transaction.reference || `TRX-${new Date(transaction.transactionDate).getFullYear()}-${String(new Date(transaction.transactionDate).getMonth() + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 999)).padStart(3, '0')}`;
  
  // Generate a description if not available
  const description = transaction.description || transaction.type;
  
  // Calculate reason and approvedBy if not provided
  const reason = transaction.reason || (
    transaction.type.includes('correction') ? 'System calculation error' : 
    transaction.type.includes('Refund') ? 'Customer request' :
    'Regular transaction'
  );
  
  const approvedBy = transaction.approvedBy || (
    transaction.type.includes('correction') ? 'Finance Manager' :
    transaction.type.includes('Refund') ? 'Customer Service' :
    'System'
  );

  // Get the transaction category
  const category = getTransactionCategory(transaction);

  // Format the date and time
  const dateTime = formatDateTime(transaction.transactionDate);

  return (
    <div 
      ref={panelRef}
      style={{
        position: 'fixed',
        top: '64px', // Adjusted to leave space for header
        right: 0,
        width: '380px',
        height: 'calc(100vh - 64px)', // Adjusted height to account for header
        backgroundColor: 'white',
        boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        overflowY: 'auto',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out', // Faster animation (0.2s instead of 0.3s) with ease-out for smoother feel
        padding: '24px',
      }}
    >
      {/* Header with close button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '24px', 
          fontWeight: 600, 
          color: '#111827',
          fontFamily: 'inherit' // Using inherited font family
        }}>
          Transaction Details
        </h2>
        <button 
          onClick={onClose} 
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Panel Content with Fade Animation */}
      <div style={{
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 0.15s ease-in-out',
      }}>
        {/* Transaction Status Badges */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          {category === 'Correction' && (
            <span style={{ 
              backgroundColor: '#eef2ff', 
              color: '#4f46e5', 
              padding: '6px 12px', 
              borderRadius: '16px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              Correction
            </span>
          )}
          <StatusBadge status={transaction.status} />
        </div>

        {/* Amount Section */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ 
            color: '#6b7280', 
            fontSize: '16px', 
            margin: '0 0 8px 0',
            fontWeight: 500,
            fontFamily: 'inherit'
          }}>
            Amount
          </p>
          <h3 style={{ 
            fontSize: '32px', 
            fontWeight: 600, 
            margin: 0,
            color: transaction.amount >= 0 ? '#047857' : '#dc2626',
            fontFamily: 'inherit'
          }}>
            {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount, transaction.currency)}
          </h3>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>

        {/* Transaction Details */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Partner</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  color: '#111827', 
                  fontSize: '15px', 
                  fontWeight: 500,
                  fontFamily: 'inherit'
                }}>
                  {transaction.partner}
                </div>
              </div>
            </div>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Category</p>
              <div style={{ 
                backgroundColor: '#f3f4f6',
                color: '#374151',
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'inline-block',
                fontFamily: 'inherit'
              }}>
                {transaction.product}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Date & Time</p>
              <div style={{ 
                color: '#111827', 
                fontSize: '15px', 
                fontWeight: 500,
                fontFamily: 'inherit'
              }}>
                {dateTime.date}, {dateTime.time}
              </div>
            </div>
            <div style={{ width: '48%' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Reference</p>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#9ca3af" style={{ width: '16px', height: '16px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5-3.9 19.5m-2.1-19.5-3.9 19.5" />
                </svg>
                <span style={{ 
                  color: '#111827', 
                  fontSize: '15px', 
                  fontWeight: 500,
                  fontFamily: 'inherit'
                }}>
                  {reference}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0', fontFamily: 'inherit' }}>Description</p>
            <div style={{ 
              color: '#111827', 
              fontSize: '15px', 
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              {description}
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div>
          <h4 style={{ 
            fontSize: '16px', 
            color: '#6b7280', 
            fontWeight: 500, 
            margin: '0 0 16px 0',
            fontFamily: 'inherit'
          }}>
            Additional Information
          </h4>

          <div style={{ 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            padding: '16px' 
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '12px' 
            }}>
              <span style={{ color: '#111827', fontWeight: 500, fontFamily: 'inherit' }}>Reason:</span>
              <span style={{ color: '#4b5563', fontFamily: 'inherit' }}>{reason}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between' 
            }}>
              <span style={{ color: '#111827', fontWeight: 500, fontFamily: 'inherit' }}>Approved By:</span>
              <span style={{ color: '#4b5563', fontFamily: 'inherit' }}>{approvedBy}</span>
            </div>
          </div>
        </div>

        {/* Transaction ID Footer */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginTop: '40px',
          color: '#9ca3af',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '16px', height: '16px', marginRight: '6px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          Transaction ID: {transaction.id.substring(0, 8)}
        </div>
      </div>
    </div>
  );
};

// NEW: Manual Correction Modal Component
interface ManualCorrectionModalProps {
  onClose: () => void;
  onSubmit: (correctionData: any) => void;
  partners: Partner[];
}

const ManualCorrectionModal: React.FC<ManualCorrectionModalProps> = ({ onClose, onSubmit, partners }) => {
  // States for multi-step form
  const [step, setStep] = React.useState(1);
  const [selectedPartner, setSelectedPartner] = React.useState<Partner | null>(null);
  const [correctionType, setCorrectionType] = React.useState<CorrectionType | ''>('');
  const [amount, setAmount] = React.useState('');
  const [currency, setCurrency] = React.useState('USD');
  const [reason, setReason] = React.useState('');
  const [commissionDate, setCommissionDate] = React.useState<Date | null>(null);
  const [commissionPercentage, setCommissionPercentage] = React.useState('');
  const [documentFile, setDocumentFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // New state for partner search
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filteredPartners, setFilteredPartners] = React.useState(partners);
  // State for date picker visibility
  const [isDatePickerVisible, setIsDatePickerVisible] = React.useState(false);
  const datePickerRef = React.useRef<HTMLDivElement>(null);
  // New state for product selection
  const [selectedProduct, setSelectedProduct] = React.useState<string>('BNPL');

  // Available currencies
  const currencies = ['USD', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'EGP', 'OMR'];
  
  // Available products
  const products = ['BNPL', 'Acquiring', 'Tabby Card', 'Capital', 'Prepaid Ads', 'Postpaid Ads'];

  // Filter partners when search term changes
  React.useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPartners(partners);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredPartners(
        partners.filter(
          partner => 
            partner.name.toLowerCase().includes(term) || 
            partner.location.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, partners]);

  // Handle clicks outside date picker
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsDatePickerVisible(false);
      }
    };

    if (isDatePickerVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatePickerVisible]);

  // Format date for display
  const formatDate = (date: Date | null): string => {
    if (!date) return 'Select Date';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    setCommissionDate(date);
    setIsDatePickerVisible(false);
  };

  // Handle partner selection
  const handlePartnerSelect = (partner: Partner) => {
    setSelectedPartner(partner);
    // Move to next step automatically after selection
    setStep(2);
  };

  // Handle correction type selection
  const handleCorrectionTypeSelect = (type: CorrectionType) => {
    setCorrectionType(type);
    // Move to next step automatically after selection
    setStep(3);
  };

  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setDocumentFile(e.target.files[0]);
    }
  };

  // Handle file removal
  const handleRemoveFile = () => {
    setDocumentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Calculate correction amount for Commission Amount correction type
  const calculateCorrectionAmount = () => {
    if (correctionType !== 'Commission Amount' || !commissionPercentage) {
      return null;
    }

    const baseAmount = 10000; // Example base amount, this would come from actual data
    const newAmount = baseAmount * (parseFloat(commissionPercentage) / 100);
    
    return {
      previous: baseAmount * 0.05, // Assuming 5% was the previous rate
      new: newAmount,
      difference: newAmount - (baseAmount * 0.05)
    };
  };

  // Handle form submission
  const handleSubmit = () => {
    const correctionData = {
      partner: selectedPartner,
      correctionType,
      amount: parseFloat(amount),
      currency,
      reason,
      commissionDate,
      commissionPercentage: commissionPercentage ? parseFloat(commissionPercentage) : undefined,
      documentFile,
      calculatedAmount: calculateCorrectionAmount(),
      product: selectedProduct // Include the selected product
    };
    onSubmit(correctionData);
  };

  // Helper to check if current step is complete
  const isStepComplete = () => {
    switch (step) {
      case 1:
        return !!selectedPartner;
      case 2:
        return !!correctionType;
      case 3:
        if (correctionType === 'Commission Start Date') {
          return !!commissionDate && !!selectedProduct && !!reason;
        } else if (correctionType === 'Commission Amount') {
          return !!commissionPercentage && !!commissionDate && !!selectedProduct && !!reason;
        }
        return !!amount && !!selectedProduct && !!reason;
      case 4:
        return true; // Document upload is optional
      default:
        return false;
    }
  };

  // Navigate to next step
  const goToNextStep = () => {
    if (isStepComplete()) {
      setStep(prev => prev + 1);
    }
  };

  // Font family for consistent styling
  const fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif";

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div style={{ padding: '24px', fontFamily }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>Select Partner</h3>
            
            {/* Search bar */}
            <div style={{ marginBottom: '20px' }}>
              <input 
                type="text"
                placeholder="Search partners by name or country"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: '#f9fafb',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
              {filteredPartners.map((partner) => (
                <div 
                  key={partner.name}
                  onClick={() => handlePartnerSelect(partner)}
                  style={{ 
                    border: `1px solid ${selectedPartner?.name === partner.name ? '#34b27b' : '#e5e7eb'}`,
                    borderRadius: '8px', 
                    padding: '16px',
                    cursor: 'pointer',
                    backgroundColor: selectedPartner?.name === partner.name ? '#f0fdf4' : 'white',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {partner.logoUrl ? (
                      <img
                        src={partner.logoUrl}
                        alt={`${partner.name} logo`}
                        style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'contain', border: '1px solid #f3f4f6' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '32px', height: '32px', borderRadius: '4px', backgroundColor: '#f3f4f6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', 
                          fontSize: '12px', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase'
                        }}
                      >
                        {partner.name.split(' ').map(n => n?.[0]).filter(Boolean).slice(0, 2).join('')}
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{partner.name}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{partner.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 2:
        return (
          <div style={{ padding: '24px', fontFamily }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>Select Correction Type</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              {['VAT Correction', 'Fee Adjustment', 'Commission Amount', 'Commission Start Date', 'Repayment'].map((type) => (
                <div 
                  key={type}
                  onClick={() => handleCorrectionTypeSelect(type as CorrectionType)}
                  style={{ 
                    border: `1px solid ${correctionType === type ? '#34b27b' : '#e5e7eb'}`,
                    borderRadius: '8px', 
                    padding: '16px',
                    cursor: 'pointer',
                    backgroundColor: correctionType === type ? '#f0fdf4' : 'white',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 500, color: '#111827' }}>{type}</div>
                    {correctionType === type && (
                      <CheckCircle size={18} color="#34b27b" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 3:
        return (
          <div style={{ padding: '24px', fontFamily }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>Enter Correction Details</h3>
            
            {/* Product Selection - Add this for all correction types */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#374151', marginBottom: '8px', fontWeight: 500 }}>
                Product
              </label>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  boxSizing: 'border-box'
                }}
              >
                {products.map(product => (
                  <option key={product} value={product}>{product}</option>
                ))}
              </select>
            </div>
            
            {correctionType === 'Commission Start Date' ? (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#374151', marginBottom: '8px', fontWeight: 500 }}>
                  New Commission Start Date
                </label>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setIsDatePickerVisible(!isDatePickerVisible)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#111827',
                      fontFamily: 'inherit'
                    }}
                  >
                    <Calendar size={16} style={{ color: '#6b7280', marginRight: '8px' }} />
                    <span>{formatDate(commissionDate)}</span>
                  </button>

                  {isDatePickerVisible && (
                    <div
                      ref={datePickerRef}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        zIndex: 10,
                      }}>
                      <DatePicker
                        selected={commissionDate}
                        onChange={handleDateChange}
                        inline
                        dateFormat="MMM dd, yyyy"
                        calendarClassName="custom-datepicker-style"
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : correctionType === 'Commission Amount' ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#374151', marginBottom: '8px', fontWeight: 500 }}>
                    Commission Rate (%)
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      value={commissionPercentage}
                      onChange={(e) => setCommissionPercentage(e.target.value)}
                      placeholder="Enter percentage"
                      min="0"
                      max="100"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '12px',
                        paddingRight: '40px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <span style={{ 
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6b7280',
                      fontSize: '14px',
                      pointerEvents: 'none'
                    }}>
                      %
                    </span>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#374151', marginBottom: '8px', fontWeight: 500 }}>
                    Commission Date
                  </label>
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setIsDatePickerVisible(!isDatePickerVisible)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#111827',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box'
                      }}
                    >
                      <Calendar size={16} style={{ color: '#6b7280', marginRight: '8px' }} />
                      <span>{formatDate(commissionDate)}</span>
                    </button>

                    {isDatePickerVisible && (
                      <div
                        ref={datePickerRef}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          zIndex: 10,
                        }}>
                        <DatePicker
                          selected={commissionDate}
                          onChange={handleDateChange}
                          inline
                          dateFormat="MMM dd, yyyy"
                          calendarClassName="custom-datepicker-style"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#374151', marginBottom: '8px', fontWeight: 500 }}>
                    Correction Amount
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      style={{
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px 0 0 8px',
                        fontSize: '14px',
                        width: '100px',
                        backgroundColor: '#fff',
                        boxSizing: 'border-box'
                      }}
                    >
                      {currencies.map(curr => (
                        <option key={curr} value={curr}>{curr}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0 8px 8px 0',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#374151', marginBottom: '8px', fontWeight: 500 }}>
                Reason for Correction
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why this correction is needed"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily,
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        );
      
      case 4:
        return (
          <div style={{ padding: '24px', fontFamily }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>Upload Supporting Document</h3>
            
            {!documentFile ? (
              <div 
                style={{ 
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  padding: '32px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  width: '100%'
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={32} color="#6b7280" style={{ margin: '0 auto 12px' }} />
                <p style={{ color: '#111827', fontWeight: 500, margin: '0 0 4px' }}>Click to upload document</p>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                  Supports PDF, JPEG, PNG up to 10MB
                </p>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div>
            ) : (
              <div 
                style={{ 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                  width: '100%'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ backgroundColor: '#f3f4f6', padding: '8px', borderRadius: '6px' }}>
                    <FileText size={20} color="#6b7280" />
                  </div>
                  <div>
                    <div style={{ color: '#111827', fontWeight: 500 }}>{documentFile.name}</div>
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>
                      {(documentFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleRemoveFile}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            )}
            
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '16px 0 0', fontStyle: 'italic' }}>
              Note: Supporting documents are optional but recommended for audit purposes.
            </p>
          </div>
        );
      
      case 5:
        // Calculate correction amount for display
        const calculatedAmount = calculateCorrectionAmount();
        
        return (
          <div style={{ padding: '24px', fontFamily }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '24px' }}>Correction Summary</h3>
            
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxSizing: 'border-box', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ color: '#6b7280', fontSize: '15px' }}>Partner</div>
                <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>{selectedPartner?.name}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ color: '#6b7280', fontSize: '15px' }}>Product</div>
                <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>{selectedProduct}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ color: '#6b7280', fontSize: '15px' }}>Correction Type</div>
                <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>{correctionType}</div>
              </div>
              
              {correctionType === 'Commission Amount' && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ color: '#6b7280', fontSize: '15px' }}>Commission Rate</div>
                    <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>{commissionPercentage}%</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ color: '#6b7280', fontSize: '15px' }}>Commission Date</div>
                    <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>
                      {commissionDate ? formatDate(commissionDate) : 'Not selected'}
                    </div>
                  </div>
                  
                  {calculatedAmount && (
                    <div style={{ 
                      marginTop: '16px', 
                      borderTop: '1px solid #e5e7eb', 
                      paddingTop: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
                        Correction Calculation
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ color: '#6b7280', fontSize: '15px' }}>Previous Commission</div>
                        <div style={{ color: '#374151', fontSize: '15px' }}>{currency} {calculatedAmount.previous.toFixed(2)}</div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ color: '#6b7280', fontSize: '15px' }}>New Commission</div>
                        <div style={{ color: '#374151', fontSize: '15px' }}>{currency} {calculatedAmount.new.toFixed(2)}</div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                        <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>Difference (Correction Amount)</div>
                        <div style={{ 
                          color: calculatedAmount.difference >= 0 ? '#047857' : '#dc2626',
                          fontWeight: 500,
                          fontSize: '15px'
                        }}>
                          {calculatedAmount.difference >= 0 ? '+' : ''}{currency} {calculatedAmount.difference.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {correctionType === 'Commission Start Date' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div style={{ color: '#6b7280', fontSize: '15px' }}>New Start Date</div>
                  <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>
                    {commissionDate ? formatDate(commissionDate) : 'Not selected'}
                  </div>
                </div>
              )}
              
              {correctionType !== 'Commission Start Date' && correctionType !== 'Commission Amount' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div style={{ color: '#6b7280', fontSize: '15px' }}>Amount</div>
                  <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>{currency} {amount}</div>
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ color: '#6b7280', fontSize: '15px' }}>Reason</div>
                <div style={{ color: '#111827', fontWeight: 500, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word', fontSize: '15px' }}>{reason}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ color: '#6b7280', fontSize: '15px' }}>Supporting Document</div>
                <div style={{ color: '#111827', fontWeight: 500, fontSize: '15px' }}>
                  {documentFile ? documentFile.name : 'None provided'}
                </div>
              </div>
            </div>
            
            <div style={{ backgroundColor: '#fef2f2', borderRadius: '8px', padding: '16px', boxSizing: 'border-box', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <AlertTriangle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ color: '#dc2626', fontWeight: 600, margin: '0 0 4px', fontSize: '15px' }}>Important Notice</p>
                  <p style={{ color: '#7f1d1d', fontSize: '15px', margin: 0, lineHeight: '1.5' }}>
                    This correction will be applied immediately and cannot be undone.
                    Please verify all information is correct before proceeding.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Determine if should show next button
  const showNextButton = step < 5;
  const showSubmitButton = step === 5;
  const showBackButton = step > 1;

  // Modal title based on step
  const getModalTitle = () => {
    switch(step) {
      case 1: return 'New Manual Correction';
      case 2: return 'Choose Correction Type';
      case 3: return 'Enter Correction Details';
      case 4: return 'Upload Supporting Document';
      case 5: return 'Review and Confirm';
      default: return 'New Manual Correction';
    }
  };

  return (
    <>
      {/* Modal Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827' }}>{getModalTitle()}</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '15px', color: '#6b7280' }}>
            Step {step} of 5
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#6b7280',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.15s ease-in-out'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Modal Body */}
      <div style={{ 
        maxHeight: 'calc(80vh - 140px)', 
        overflowY: 'auto',
        fontFamily
      }}>
        {renderStepContent()}
      </div>

      {/* Modal Footer */}
      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #e5e7eb', 
        display: 'flex', 
        justifyContent: 'space-between', 
        backgroundColor: '#f9fafb',
        fontFamily
      }}>
        <div>
          {showBackButton && (
            <button
              onClick={() => setStep(prev => prev - 1)}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                fontSize: '15px',
                fontWeight: 500,
                color: '#374151',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease-in-out'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
              Back
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              fontSize: '15px',
              fontWeight: 500,
              color: '#374151',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            Cancel
          </button>
          {showNextButton && (
            <button
              onClick={goToNextStep}
              disabled={!isStepComplete()}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid #34b27b',
                backgroundColor: isStepComplete() ? '#34b27b' : '#a5f3c4',
                fontSize: '15px',
                fontWeight: 500,
                color: 'white',
                cursor: isStepComplete() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.15s ease-in-out'
              }}
              onMouseEnter={(e) => {
                if (isStepComplete()) {
                  e.currentTarget.style.backgroundColor = '#2da26c';
                }
              }}
              onMouseLeave={(e) => {
                if (isStepComplete()) {
                  e.currentTarget.style.backgroundColor = '#34b27b';
                }
              }}
            >
              Next
              <ArrowRight size={16} />
            </button>
          )}
          {showSubmitButton && (
            <button
              onClick={handleSubmit}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid #34b27b',
                backgroundColor: '#34b27b',
                fontSize: '15px',
                fontWeight: 500,
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.15s ease-in-out'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2da26c'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#34b27b'}
            >
              Submit Correction
              <CheckCircle size={16} />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// NEW: Processing Component
interface CorrectionProcessingModalProps {
  onFinish: () => void;
  correction?: {
    partner?: { name: string; location: string };
    correctionType?: string;
    amount?: number;
    currency?: string;
    calculatedAmount?: {
      previous: number;
      new: number;
      difference: number;
    } | null;
  };
}

const CorrectionProcessingModal: React.FC<CorrectionProcessingModalProps> = ({ onFinish, correction }) => {
  const [isComplete, setIsComplete] = React.useState(false);
  const [completionTimestamp, setCompletionTimestamp] = React.useState<string | null>(null);

  // Font family for consistent styling
  const fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif";

  React.useEffect(() => {
    // Simulate processing delay
    const timer = setTimeout(() => {
      setIsComplete(true);
      setCompletionTimestamp(new Date().toLocaleString());
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* Modal Header */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', fontFamily }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
          {isComplete ? 'Correction Applied' : 'Processing Correction'}
        </h2>
        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
          {isComplete 
            ? 'The manual correction has been successfully applied' 
            : 'Please wait while we process your correction request'
          }
        </p>
      </div>

      {/* Modal Body */}
      <div style={{ padding: '24px 32px', minHeight: '200px', fontFamily }}>
        {isComplete ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#ecfdf5',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <CheckCircle size={40} color="#34b27b" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
              Correction Successfully Applied
            </h3>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '20px', maxWidth: '450px', margin: '0 auto 20px' }}>
              The manual correction has been applied to the account and will be visible in the transaction history.
            </p>
            
            <div style={{
              backgroundColor: '#f9fafb', 
              border: '1px solid #e5e7eb',
              borderRadius: '8px', 
              padding: '16px', 
              display: 'inline-block',
              textAlign: 'left',
              margin: '0 auto 24px',
              minWidth: '300px',
              maxWidth: '400px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Partner</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                  {correction?.partner?.name || 'Selected Partner'}
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Correction Type</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                  {correction?.correctionType || 'Correction'}
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Reference ID</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                  COR-{Date.now().toString().slice(-9)}
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Completed At</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                  {completionTimestamp}
                </div>
              </div>
              
              {/* Display calculated amount for Commission Amount corrections */}
              {correction?.calculatedAmount && (
                <div style={{ 
                  marginTop: '12px', 
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e7eb',
                }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
                    Correction Amount
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>Previous</div>
                    <div style={{ fontSize: '13px', color: '#374151' }}>
                      {correction.currency} {correction.calculatedAmount.previous.toFixed(2)}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>New</div>
                    <div style={{ fontSize: '13px', color: '#374151' }}>
                      {correction.currency} {correction.calculatedAmount.new.toFixed(2)}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>Difference</div>
                    <div style={{ 
                      fontSize: '14px',
                      fontWeight: 500,
                      color: correction.calculatedAmount.difference >= 0 ? '#047857' : '#dc2626'
                    }}>
                      {correction.calculatedAmount.difference >= 0 ? '+' : ''}
                      {correction.currency} {correction.calculatedAmount.difference.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Simple amount for non-calculated corrections */}
              {correction?.amount && !correction.calculatedAmount && (
                <div style={{ 
                  marginTop: '12px', 
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                    Correction Amount
                  </div>
                  <div style={{ 
                    fontSize: '14px',
                    fontWeight: 500,
                    color: (correction.amount >= 0) ? '#047857' : '#dc2626'
                  }}>
                    {correction.amount >= 0 ? '+' : ''}
                    {correction.currency} {correction.amount.toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0' }}>
            <div style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%', 
              backgroundColor: '#eff6ff',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <div style={{ animation: 'spin 2s linear infinite' }}>
                <RefreshCw size={30} color="#3b82f6" />
              </div>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              Processing your request
            </h3>
            <p style={{ fontSize: '15px', color: '#6b7280', textAlign: 'center' }}>
              Please wait while we apply the manual correction to the account.
            </p>
          </div>
        )}
      </div>

      {/* Modal Footer */}
      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #e5e7eb', 
        display: 'flex', 
        justifyContent: 'flex-end',
        backgroundColor: '#f9fafb',
        fontFamily
      }}>
        {isComplete && (
          <button
            onClick={onFinish}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #34b27b',
              backgroundColor: '#34b27b',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2da26c'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#34b27b'}
          >
            Close
          </button>
        )}
      </div>

      {/* Add keyframe animation for spinning icon */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

const TransactionsPage: React.FC = () => {
  // *** UPDATED: Use the pre-processed and sorted data ***
  const [transactions, setTransactions] = React.useState<Transaction[]>(processedMockTransactions);
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  // --- NEW: State for ID copy hover and feedback ---
  const [hoveredIdIcon, setHoveredIdIcon] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  // --- NEW: Filter States ---
  const [selectedDateRange, setSelectedDateRange] = React.useState<string>('All Time');
  const [selectedProduct, setSelectedProduct] = React.useState<string>('All Products');
  const [selectedType, setSelectedType] = React.useState<string>('All Types');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('All Countries');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('All Statuses');
  // --- NEW: Custom Date State ---
  const [customStartDate, setCustomStartDate] = React.useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = React.useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = React.useState<boolean>(false);

  // --- NEW: Modal State ---
  const [isExportModalOpen, setIsExportModalOpen] = React.useState<boolean>(false);
  // --- NEW: Manual Correction Modal State ---
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = React.useState<boolean>(false);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = React.useState<boolean>(false);
  // Add state for correction data
  const [correctionData, setCorrectionData] = React.useState<any>(null);

  // --- NEW: Selected transaction state ---
  const [selectedTransaction, setSelectedTransaction] = React.useState<Transaction | null>(null);
  // --- NEW: Panel visibility state for animation ---
  const [isPanelVisible, setIsPanelVisible] = React.useState<boolean>(false);
  // --- NEW: Content fade key for transaction changes ---
  const [contentFadeKey, setContentFadeKey] = React.useState<string>('');

  // --- NEW: Create partners list from the available transactions ---
  const partners = React.useMemo(() => {
    const uniquePartners = new Map<string, Partner>();
    
    transactions.forEach(tx => {
      if (!uniquePartners.has(tx.partner)) {
        uniquePartners.set(tx.partner, {
          name: tx.partner,
          location: tx.location || 'N/A',
          logoUrl: tx.logoUrl || null
        });
      }
    });
    
    return Array.from(uniquePartners.values());
  }, [transactions]);

  // --- UPDATED: Derive Filter Options ---
  const productOptions = React.useMemo(() => ['All Products', ...Array.from(new Set(transactions.map(tx => tx.product)))], [transactions]);
  const typeOptions = React.useMemo(
    () => [
      'All Types',
      ...Array.from(new Set(transactions.map((tx) => tx.type))).sort(), // Sort alphabetically
    ],
    [transactions]
  );
  const countryOptions = React.useMemo(() => ['All Countries', ...Array.from(new Set(transactions.map(tx => tx.location || 'N/A')))], [transactions]);
  const statusOptions = React.useMemo(() => ['All Statuses', 'Completed', 'Pending', 'Failed'], []);
  const dateRangeOptions = ['All Time', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom date']; // Added "Custom date"

  // --- UPDATED: Filtering Logic ---
  const filteredTransactions = React.useMemo(() => {
      // ... existing filtering logic
      return transactions.filter(tx => {
          // ... existing filter implementation
          // (Keep all the existing filtering logic from before)
          const txDate = new Date(tx.transactionDate);

          // Date Range Filter
          let dateMatch = true;
          if (selectedDateRange === 'Custom date') {
              // Only filter if both dates are set
              if (customStartDate && customEndDate) {
                  // Adjust end date to include the whole day
                  const adjustedEndDate = new Date(customEndDate);
                  adjustedEndDate.setHours(23, 59, 59, 999);
                  dateMatch = txDate >= customStartDate && txDate <= adjustedEndDate;
              } else if (customStartDate) {
                  // If only start date is selected, filter from start date onwards
                   dateMatch = txDate >= customStartDate;
              } else {
                // If custom is selected but dates aren't set yet, don't filter by date (or show all)
                dateMatch = true;
              }
          } else if (selectedDateRange === 'Last 7 Days') {
              const startDate = new Date();
              startDate.setDate(startDate.getDate() - 7);
              dateMatch = txDate >= startDate;
          } else if (selectedDateRange === 'Last 30 Days') {
              const startDate = new Date();
              startDate.setDate(startDate.getDate() - 30);
              dateMatch = txDate >= startDate;
          } else if (selectedDateRange === 'Last 90 Days') {
              const startDate = new Date();
              startDate.setDate(startDate.getDate() - 90);
              dateMatch = txDate >= startDate;
          }

          // Product Filter
          const productMatch = selectedProduct === 'All Products' || tx.product === selectedProduct;

          // Type Filter
          const typeMatch = selectedType === 'All Types' || tx.type === selectedType;

          // Country Filter
          const countryMatch = selectedCountry === 'All Countries' || tx.location === selectedCountry;

          // Status Filter
          const statusMatch = selectedStatus === 'All Statuses' || tx.status === selectedStatus;

          // Search Term Filter
          const matchesSearch = searchTerm === '' ||
                                tx.partner.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.amount.toString().includes(searchTerm) || // Search amount
                                tx.currency.toLowerCase().includes(searchTerm.toLowerCase()) || // Search currency
                                (tx.location && tx.location.toLowerCase().includes(searchTerm.toLowerCase())); // Search location

          return dateMatch && productMatch && typeMatch && countryMatch && statusMatch && matchesSearch;
      });
  }, [transactions, searchTerm, selectedDateRange, selectedProduct, selectedType, selectedCountry, selectedStatus, customStartDate, customEndDate]); // Add custom date states to dependency array

  // --- NEW: Handlers for Modal ---
  const openExportModal = () => setIsExportModalOpen(true);
  const closeExportModal = () => setIsExportModalOpen(false);
  
  // --- NEW: Handlers for Manual Correction Modal ---
  const openCorrectionModal = () => {
    console.log('Opening correction modal');
    setIsCorrectionModalOpen(true);
    console.log('State updated:', isCorrectionModalOpen); // This will still show false due to state update batching
  };
  const closeCorrectionModal = () => setIsCorrectionModalOpen(false);
  
  // --- NEW: Handler for Correction Submission ---
  const handleCorrectionSubmit = (correctionData: any) => {
    // Store the correction data for use in the processing modal
    setCorrectionData(correctionData);
    
    // Close correction modal and show processing
    closeCorrectionModal();
    setIsProcessingModalOpen(true);
  };
  
  // --- NEW: Handler for Correction Processing Completion ---
  const handleProcessingComplete = () => {
    setIsProcessingModalOpen(false);
    
    // Generate a new transaction for the correction
    const now = new Date();
    
    // Use stored correction data to create the transaction
    let correctionAmount = 0;
    if (correctionData?.calculatedAmount) {
      correctionAmount = correctionData.calculatedAmount.difference;
    } else if (correctionData?.amount) {
      correctionAmount = correctionData.amount;
    }
    
    const newTransaction: Transaction = {
      id: generateUUID(),
      transactionDate: now.toISOString(),
      partner: correctionData?.partner?.name || 'Selected Partner',
      product: correctionData?.product || 'BNPL', // Use the selected product from correction data
      type: correctionData?.correctionType || 'Correction',
      amount: correctionAmount,
      currency: correctionData?.currency || 'USD',
      status: 'Completed',
      location: correctionData?.partner?.location || 'N/A',
      logoUrl: correctionData?.partner?.logoUrl || null,
      category: 'Correction',
      reference: `COR-${Date.now().toString().slice(-9)}`,
      description: correctionData?.reason || 'Manual correction applied',
      reason: correctionData?.reason || 'Manual adjustment',
      approvedBy: 'Finance Manager',
      correctionDetails: {
        correctionType: correctionData?.correctionType || 'Fee Adjustment',
        reason: correctionData?.reason || 'Billing error correction',
      }
    };
    
    // Clear correction data
    setCorrectionData(null);
    
    // Add the new transaction to the list
    setTransactions(prev => [newTransaction, ...prev]);
  };

  // --- UPDATED: Handler for Custom Date Change ---
   const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setCustomStartDate(start);
        setCustomEndDate(end);
        // Optionally close picker after selection, or keep it open
        // setIsDatePickerVisible(false);
    };

  // --- UPDATED: Handler for Date Dropdown Change ---
   const handleDateRangeChange = (value: string) => {
        setSelectedDateRange(value);
        if (value === 'Custom date') {
            setIsDatePickerVisible(true); // Show date picker
            // Keep existing custom dates or reset them? Let's keep them for now.
            // setCustomStartDate(null);
            // setCustomEndDate(null);
        } else {
            setIsDatePickerVisible(false); // Hide date picker
            setCustomStartDate(null); // Reset custom dates when switching away
            setCustomEndDate(null);
        }
    };

  // --- UPDATED: Handler for copying ID ---
  const handleCopyID = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500); // Reset icon after 1.5 seconds
    } catch (err) {
      console.error('Failed to copy ID: ', err);
      // Optionally: show an error message to the user
    }
  };

  // --- UPDATED: Handler for row click ---
  const handleRowClick = (transaction: Transaction) => {
    if (selectedTransaction) {
      // If a transaction is already selected (panel is open), just update the transaction
      // and trigger content fade animation with new key
      setContentFadeKey(transaction.id);
      setSelectedTransaction(transaction);
    } else {
      // First time opening the panel
      setSelectedTransaction(transaction);
      setContentFadeKey(transaction.id);
      // Set panel to visible with slight delay for proper animation when opening
      setTimeout(() => setIsPanelVisible(true), 10);
    }
  };

  // --- UPDATED: Handler to close the details panel ---
  const handleCloseDetailsPanel = () => {
    // First hide panel with animation
    setIsPanelVisible(false);
    // Then remove transaction data after animation completes (reduced from 300ms to 200ms for faster animation)
    setTimeout(() => setSelectedTransaction(null), 200);
  };

  return (
    <MainLayout>
      {/* Basic page container with padding */}
      <div style={{ padding: '24px' }}>
        {/* Header Area */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: '1.2' }}>Transactions</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>View and manage all transactions.</p>
          </div>
        </div>

        {/* Main Content Area - Table Section */}
        <section style={{ marginTop: '24px', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' /* Added overflow hidden */ }}>
          {/* Table Header Area (Filters/Search) */}
          <div style={{
              padding: '0px 0px 16px 0px', // RE-ADDED padding to outer container, removed top padding
              borderBottom: '0px solid rgb(229, 231, 235)', // REMOVED border
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap', // Allow wrapping
              gap: '16px' // Add gap between filter group and button
          }}>
              {/* Filter Group (Left Aligned) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' /* REMOVED padding here */ }}>
                  {/* NEW: Filters */}
                  <DropdownFilter
                      label="Date"
                      options={dateRangeOptions}
                      selectedValue={selectedDateRange}
                      onChange={handleDateRangeChange}
                  />
                  {/* --- NEW: Conditionally render DatePicker --- */}
                   {isDatePickerVisible && (
                       <div style={{ marginLeft: '-6px' /* Adjust positioning if needed */ }}>
                           <RangeDatePicker
                               startDate={customStartDate}
                               endDate={customEndDate}
                               onChange={handleCustomDateChange}
                           />
                       </div>
                   )}
                   <DropdownFilter
                      label="Product"
                      options={productOptions}
                      selectedValue={selectedProduct}
                      onChange={setSelectedProduct}
                  />
                   <DropdownFilter
                      label="Type"
                      options={typeOptions}
                      selectedValue={selectedType}
                      onChange={setSelectedType}
                  />
                   <DropdownFilter
                      label="Country"
                      options={countryOptions}
                      selectedValue={selectedCountry}
                      onChange={setSelectedCountry}
                  />
                   <DropdownFilter
                      label="Status"
                      options={statusOptions}
                      selectedValue={selectedStatus}
                      onChange={setSelectedStatus}
                  />
              </div>
              {/* Button Group (Right Aligned) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
                 {/* Add Manual Correction Button */}
                 <button
                    onClick={() => {
                      console.log('Button clicked');
                      setIsCorrectionModalOpen(true);
                    }}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        backgroundColor: '#f9fafb',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                 >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '16px', height: '16px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add manual correction
                 </button>
                 {/* Export Button */}
                 <button
                      onClick={openExportModal}
                      style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          backgroundColor: '#f9fafb',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
                          // marginLeft: 'auto' // REMOVED marginLeft auto, handled by wrapper div
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '16px', height: '16px' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      Export
                  </button>
              </div>
          </div>

          {/* Table */}
          {filteredTransactions.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' /* Reduced font size */ }}>
              <thead style={{ backgroundColor: '#f9fafb' /* Light background for header */ }}>
                <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                  {/* --- Columns --- */}
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>ID</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Created at</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Partner</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Country</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Product</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Category</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Type</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'right' }}>Amount</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, index) => (
                  <tr
                    key={tx.id}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      backgroundColor: hoveredRowIndex === index ? '#f9fafb' : 'transparent',
                      transition: 'background-color 0.1s ease-in-out',
                      height: '35px' // Explicitly set target height for consistency
                    }}
                    data-transaction-row="true"
                    onMouseEnter={() => setHoveredRowIndex(index)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    onClick={() => handleRowClick(tx)} // NEW: Add click handler
                  >
                    {/* --- Cells --- */}
                    {/* ID Cell */}
                    <td style={{ padding: '7px 16px', width: '60px', textAlign: 'center' }}>
                       <div
                         style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%', /* Fill cell height */ position: 'relative' /* Needed for text positioning */ }}
                         onClick={(e) => {
                           e.stopPropagation(); // Prevent row click
                           handleCopyID(tx.id);
                         }}
                         title={copiedId === tx.id ? 'ID copied' : `Click to copy ID: ${tx.id}`}
                         onMouseEnter={() => setHoveredIdIcon(tx.id)} // Added hover state for icon visibility
                         onMouseLeave={() => setHoveredIdIcon(null)} // Clear hover state
                       >
                         {
                           copiedId === tx.id ? (
                             <CheckIcon style={{ color: '#10b981' }} size={16} /> // Use CheckIcon for feedback
                           ) : hoveredIdIcon === tx.id ? (
                             <CopyIcon style={{ color: '#6b7280' }} size={14} /> // Show CopyIcon on hover
                           ) : (
                             <HashIcon style={{ color: '#9ca3af' }} size={14} /> // Default HashIcon
                           )
                         }
                       </div>
                    </td>
                    {/* Created at Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', whiteSpace: 'nowrap', width: '140px', fontWeight: 400, textAlign: 'left' }}>
                        {`${formatDateTime(tx.transactionDate).date}, ${formatDateTime(tx.transactionDate).time}`}
                    </td>
                    {/* Partner Cell */}
                    <td style={{ padding: '7px 16px', textAlign: 'left' }}>
                      <div>
                        <div style={{ fontWeight: 400, color: '#111827' }}>{tx.partner}</div>
                      </div>
                    </td>
                    {/* Country Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', width: '80px', textAlign: 'left' }}>{tx.location || 'N/A'}</td>
                    {/* Product Cell */}
                    <td style={{ padding: '7px 16px', textAlign: 'left' }}>
                       <span style={{
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          // marginRight: '5px', // Removed margin, handled by TD padding
                          fontSize: '12px',
                          display: 'inline-block',
                          marginBottom: '0px'
                       }}>
                           {tx.product}
                       </span>
                    </td>
                    {/* Category Cell */}
                    <td style={{ padding: '7px 16px', textAlign: 'left' }}>
                        <CategoryBadge category={getTransactionCategory(tx)} />
                    </td>
                    {/* Type Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 400, color: '#111827', textAlign: 'left' }}>{tx.type}</td>
                    {/* Amount Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 500, color: '#111827', textAlign: 'right' }}>
                      {formatCurrency(tx.amount, tx.currency)}
                    </td>
                    {/* Status Cell */}
                    <td style={{ padding: '7px 16px', textAlign: 'center' }}>
                      <StatusBadge status={tx.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
             // Empty State
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280',
              borderTop: '1px solid #e5e7eb', // Only show border if table would have existed
              margin: '0 24px' // Match horizontal padding of filters/header
            }}>
               <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#d1d5db" style={{ width: '48px', height: '48px', marginBottom: '16px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.03 1.126 0 1.131.094 1.976 1.057 1.976 2.192V7.5M8.25 7.5h7.5M8.25 7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H8.25Z" />
                </svg>
              <p style={{ fontSize: '16px', fontWeight: 500, color: '#111827', marginBottom: '4px 0' }}>No transactions found</p>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Try adjusting your search or filter criteria.</p>
            </div>
           )}
        </section>
      </div>

      {/* --- UPDATED: Render Transaction Details Panel conditionally --- */}
      {selectedTransaction && (
        <TransactionDetailsPanel 
          transaction={selectedTransaction} 
          onClose={handleCloseDetailsPanel}
          isVisible={isPanelVisible}
          contentFadeKey={contentFadeKey}
        />
      )}

      {/* --- Render Export Modal Conditionally --- */}
      {isExportModalOpen && (
        <ExportReportModal onClose={closeExportModal} />
      )}

      {/* --- Render Manual Correction Modal Conditionally --- */}
      {isCorrectionModalOpen && (
        <ModalBackdrop onClose={closeCorrectionModal}>
          <ManualCorrectionModal
            onClose={closeCorrectionModal}
            onSubmit={handleCorrectionSubmit}
            partners={partners}
          />
        </ModalBackdrop>
      )}
      
      {/* --- Render Processing Modal Conditionally --- */}
      {isProcessingModalOpen && (
        <ModalBackdrop onClose={() => {}} preventClose={true}>
          <CorrectionProcessingModal
            onFinish={handleProcessingComplete}
            correction={correctionData}
          />
        </ModalBackdrop>
      )}
    </MainLayout>
  );
};

export default TransactionsPage;
